import React, { useState } from 'react';
import marvisLogo from './marvis_logo.png';

// Reusable prompt a user can paste into any other LLM so it generates a new
// conversation in the exact format this tool parses. Kept as a single source of
// truth that is shown in the UI and mirrored in the README.
const LLM_PRE_PROMPT = `You are helping author a simulated conversation for "Marvis", an AI network assistant. Produce ONLY a script in the exact format described below — no preamble, no explanations, no closing remarks.

STRUCTURE
- The script is a series of question/response turns.
- Put a line containing only  ---Q  before each user question.
- Put a line containing only  ---R  before each assistant response.

  ---Q
  <the user's question in plain language>

  ---R
  <the assistant's response>

AGENT / TOOL BLOCKS (inside an ---R response)
- A response may contain one or more "agent blocks". Each block simulates a specialist/tool call and starts with these three lines:

  AGENT: <short specialist name, e.g. BGP State Analysis Specialist>
  TOOLS: <comma-separated tool names, e.g. device_cli_runner, bgp_session_inspector>
  THINKING: <one sentence on what this step is doing>

- Immediately after those three lines, write the natural-language answer for that step.
- Use SEVERAL agent blocks in one ---R to simulate a staged, multi-tool investigation: each block runs after the previous one, like a chain of tool calls that builds toward a conclusion.
- To imply work happening in parallel, say so in the THINKING line (e.g. "running X in sequence with a parallel branch for Y").

SUPPORTED FORMATTING (inside the answer text)
- Paragraphs: plain lines.
- Bold: wrap text in double asterisks, **like this**.
- Section titles: start a line with "## " (e.g. "## 1. Drop the peer") — rendered as a bold heading; number them for steps.
- Bullets: start a line with "- ". Labelled bullets: "- **Label:** detail".
- Tables: standard markdown pipe tables with a |---|---| separator row (ideal for metrics or comparisons).
- Code / CLI: fenced code blocks (a line of three backticks to open and to close).
- Plain http/https URLs auto-link.

STYLE
- Be realistic: use concrete device names, interfaces, counts and thresholds.
- End an investigation with a clear **Root cause** and a **Recommended resolution**.

SKELETON TO FOLLOW

  ---Q
  <user problem in plain language>

  ---R
  AGENT: <Specialist 1>
  TOOLS: tool_a, tool_b
  THINKING: <what step 1 checks>

  <findings for step 1, optionally a table>

  AGENT: <Specialist 2>
  TOOLS: tool_c
  THINKING: <what step 2 checks>

  <findings for step 2>

  **Root cause**
  <one-paragraph root cause>

  **Recommended resolution**
  ## 1. <first action>
  <detail>

Now write the script for this scenario:
<DESCRIBE YOUR SCENARIO — the problem, the fabric/devices involved, and the fix you want Marvis to reach>`;

const MarvisChatConfigurator = () => {
  const [markdownInput, setMarkdownInput] = useState(`---Q
we're seeing intermittent app reachability in one part of the fabric. some hosts are reachable, others fail randomly. can you investigate the evpn type-5 anomalies and tell me root cause plus fix?

---R
AGENT: EVPN Reachability Investigation Specialist
TOOLS: apstra_anomaly_feed, apstra_missing_routes_by_device_vni, hpe_ticket_search, device_cli_runner, route_capacity_analyzer, bgp_peer_route_counter
THINKING: Running a staged EVPN Type-5 investigation in sequence, with a parallel branch for known-issue search and BGP/EVPN peer analysis.

I've reviewed the Apstra EVPN and VXLAN anomaly feed first. The pattern is concentrated on **DC1-LEAF-05**: routes are being advertised by peers for the same VNIs, but this specific leaf is failing to install them locally. Other leaves on the same VNIs are showing zero or near-zero misses, which points to a single-device install failure rather than a fabric-wide control-plane issue.

I then grouped missing-route counts by system and VNI, and the asymmetry is clear.

| Device       | VNI   | Missing Routes |
|--------------|-------|----------------|
| DC1-LEAF-05  | 12010 | 31             |
| DC1-LEAF-05  | 12020 | 27             |
| DC1-LEAF-05  | 12030 | 24             |
| DC1-LEAF-03  | 12010 | 0              |
| DC1-LEAF-04  | 12010 | 0              |
| DC1-LEAF-06  | 12020 | 0              |

AGENT: JTAC Case Correlation Specialist
TOOLS: jtac_case_index, hpe_ticket_search, pattern_match_engine
THINKING: Checking prior cases for EVPN Type-5 installation failures tied to RIB/FIB exhaustion and route leakage patterns.

I checked historical support signatures for this platform. The match is strong with prior incidents where BGP sessions remained up, but one peer delivered an abnormally large route set that saturated local forwarding resources and blocked EVPN Type-5 route installation.

AGENT: BGP State Analysis Specialist
TOOLS: device_cli_runner, bgp_session_inspector, evpn_state_diff
THINKING: Validating whether this is a control-plane adjacency failure or an over-capacity route-ingestion failure.

On DC1-LEAF-05, core BGP sessions are established and not hard-down, but EVPN route visibility is incomplete for impacted VNIs. That confirms this is not a simple peering outage; it is a selective route installation failure under load.

AGENT: Route Table Capacity Specialist
TOOLS: route_capacity_analyzer, device_cli_runner, pfe_table_checker
THINKING: Comparing active route volume against practical RIB/PFE limits to confirm table exhaustion behavior.

Resource and forwarding-table checks on DC1-LEAF-05 confirm route-scale pressure.

| Metric                              | Observed        | Status        |
|-------------------------------------|-----------------|---------------|
| Active routes (inet.0 + evpn.0)     | 1,021,884       | Critical high |
| RIB utilisation                      | 99%             | Near full     |
| PFE route table utilisation          | 98-100%         | Saturated     |
| Control-plane memory                 | Elevated stable | Pressured     |

AGENT: Peer Route Source Isolation Specialist
TOOLS: bgp_peer_route_counter, route_receive_protocol_parser
THINKING: Measuring per-peer received route counts to identify which BGP session is consuming route-table capacity.

Per-peer received route counts isolate the source of exhaustion.

| Peer IP      | Received Route Count |
|--------------|----------------------|
| 172.16.0.14  | 338                  |
| 172.16.0.15  | 338                  |
| 10.80.101.221 | 900,000+ (spike pattern) |

Neighbor **10.80.101.221** is the offending source and, during flood windows, sends a massive route set into DC1-LEAF-05 consistent with internet-scale leakage. That volume consumes local RIB/PFE capacity and prevents legitimate EVPN Type-5 fabric routes from being installed.

AGENT: Routing Policy Validation Specialist
TOOLS: apstra_policy_audit, bgp_neighbor_policy_check, intended_state_validator
THINKING: Verifying whether inbound route filters/prefix limits are applied on the offending peer and whether intended state enforces protection.

Current policy posture indicates the peer can advertise a broader prefix set than required for the fabric, with insufficient inbound guardrails on that session. This allows flood events from 10.80.101.221 to pressure the local route table.

**Root cause analysis**

A BGP session on **DC1-LEAF-05** is receiving more routes from peer **10.80.101.221** than its routing table has space for.
This has exhausted the hardware forwarding table. EVPN Type-5 routes from fabric peers cannot be installed, causing selective reachability failures for hosts on this leaf.

**Recommended resolution**

## 1. Drop the offending BGP peer completely in Apstra

Remove the Connectivity Template that establishes the BGP peering to **10.80.101.221**. This is an intentional hard removal of the session from intended state, not just a temporary CLI clear.

Expected immediate outcome: route pressure drops, EVPN Type-5 routes can be reinstalled, and missing-route anomalies trend back toward zero.

## 2. Create a new Apstra routing policy before allowing the peer back

Create an import routing policy in Apstra that permits only the specific routes you want to accept from this peer. Do not allow unrestricted import from 10.80.101.221. Attach this policy to the rebuilt peering so only approved prefixes are admitted into the fabric.

Routing policy reference: - https://www.juniper.net/documentation/us/en/software/apstra6.1/apstra-user-guide/topics/concept/routing-policies.html

**Two operational outcomes**

- **Hard delete path:** keep the BGP peering removed while you investigate exactly what the peer is sending, then reintroduce only after policy controls are defined and reviewed.
- **Controlled reintroduction path:** re-add the peering with the new import policy applied from day one, so accepted routes are constrained to expected prefixes only.

This keeps remediation targeted, prevents recurrence from over-broad route import, and preserves Apstra as the source of truth.
`);
  
  const [jsOutput, setJsOutput] = useState('');
  const [inputSpeed, setInputSpeed] = useState(25);
  const [responseSpeed, setResponseSpeed] = useState(15);
  const [pauseTime, setPauseTime] = useState(200);
  const [stageTime, setStageTime] = useState(200);
  const [inputSelector, setInputSelector] = useState('');
  const [agentImageHref, setAgentImageHref] = useState('');
  const [error, setError] = useState('');

  const getResolvedAgentImageHref = async () => {
    if (agentImageHref.trim() !== '') {
      return agentImageHref;
    }

    const response = await fetch(marvisLogo);
    const blob = await response.blob();

    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = () => reject(new Error('Failed to read image as data URL'));
      reader.readAsDataURL(blob);
    });

    if (!dataUrl) {
      throw new Error('Unable to convert marvis_logo.png to base64 data URL');
    }

    setAgentImageHref(dataUrl);
    return dataUrl;
  };

  const convertMarkdownToHtml = (markdown) => {
    let html = '';
    const lines = markdown.split('\n');
    let inList = false;
    let inOrderedSection = false;
    let inCodeBlock = false;
    let codeBlockContent = '';
    
    // Helper function to convert inline bold text
      const autoLinkUrls = (text) => {
        return text.replace(new RegExp('https?:\\/\\/[^ <]+', 'g'), (url) => {
          const match = url.match(new RegExp('^(.*?)([.,!?;:)]*)$'));
          const href = match ? match[1] : url;
          const trailing = match ? match[2] : '';
          return `<a href="${href}" target="_blank" rel="noopener noreferrer">${href}</a>${trailing}`;
        });
      };

      const convertInlineBold = (text) => {
        return autoLinkUrls(text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'));
      };
    const isRawHtmlLine = (text) => /^<\/?[a-zA-Z][^>]*>/.test(text);

    const isTableRow = (text) => /^\|(.+\|)+$/.test(text);
    const isTableSeparator = (text) => /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(text);
    const parseTableCells = (text) => text.replace(/^\||\|$/g, '').split('|').map(cell => cell.trim());

    const closeListIfNeeded = () => {
      if (inList) {
        html += '</ul>\n';
        inList = false;
      }
    };
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      // Handle code blocks
      if (trimmedLine.startsWith('```')) {
        if (!inCodeBlock) {
          // Start of code block
          inCodeBlock = true;
          codeBlockContent = '';
          closeListIfNeeded();
        } else {
          // End of code block
          html += `<pre style="border: 1px solid #d1d5db; border-radius: 4px; padding: 12px; background-color: #f9fafb; font-family: monospace; overflow-x: auto;"><code>${codeBlockContent}</code></pre>\n`;
          inCodeBlock = false;
          codeBlockContent = '';
        }
        continue;
      }
      
      if (inCodeBlock) {
        codeBlockContent += line + '\n';
        continue;
      }

      if (isRawHtmlLine(trimmedLine)) {
        closeListIfNeeded();
        html += line + '\n';
        continue;
      }

      // Handle markdown tables
      if (isTableRow(trimmedLine) && i + 1 < lines.length && isTableSeparator(lines[i + 1].trim())) {
        closeListIfNeeded();

        const headers = parseTableCells(trimmedLine);
        const rows = [];
        const computedColumnWidth = Math.max(100 / Math.max(headers.length, 1), 33.333);
        i += 2;

        while (i < lines.length) {
          const rowLine = lines[i].trim();
          if (!isTableRow(rowLine) || isTableSeparator(rowLine)) {
            i--;
            break;
          }
          rows.push(parseTableCells(rowLine));
          i++;
        }

        html += '<table style="width: 100%; table-layout: fixed; border-collapse: collapse;">\n<thead>\n<tr>';
        headers.forEach((header) => {
          html += `<th style="width: ${computedColumnWidth}%; min-width: 33.333%;">${convertInlineBold(header)}</th>`;
        });
        html += '</tr>\n</thead>\n<tbody>\n';

        rows.forEach((row) => {
          html += '<tr>';
          row.forEach((cell) => {
            html += `<td style="width: ${computedColumnWidth}%; min-width: 33.333%;">${convertInlineBold(cell)}</td>`;
          });
          html += '</tr>\n';
        });

        html += '</tbody>\n</table>\n';
        continue;
      }
      
      // Skip empty lines
      if (!trimmedLine) {
        closeListIfNeeded();
        continue;
      }
      
      // Skip agent/tools/thinking metadata lines - these are handled separately
      if (trimmedLine.startsWith('AGENT:') || 
          trimmedLine.startsWith('TOOLS:') || 
          trimmedLine.startsWith('THINKING:')) {
        continue;
      }
      
      // Handle H2 headings as bold title lines
      if (trimmedLine.startsWith('## ')) {
        closeListIfNeeded();
        const title = trimmedLine.substring(3);
        html += `<p><strong>${title}</strong></p>\n`;
      }
      // Handle bullet points with bold labels
      else if (trimmedLine.startsWith('- **') && trimmedLine.includes(':**')) {
        if (!inList) {
          html += '<ul>\n';
          inList = true;
        }
        const colonPos = trimmedLine.indexOf(':**');
        if (colonPos > 4) {
          const label = trimmedLine.substring(4, colonPos);
          const text = convertInlineBold(trimmedLine.substring(colonPos + 4));
          html += `<li style="margin-left: 20px;"><strong>${label}:</strong> ${text}</li>\n`;
        } else {
          const text = convertInlineBold(trimmedLine.substring(2));
          html += `<li style="margin-left: 20px;">${text}</li>\n`;
        }
      }
      // Handle regular bullet points
      else if (trimmedLine.startsWith('- ')) {
        if (!inList) {
          html += '<ul>\n';
          inList = true;
        }
        const text = convertInlineBold(trimmedLine.substring(2));
        html += `<li style="margin-left: 20px;">${text}</li>\n`;
      }
      // Handle regular paragraphs
      else {
        closeListIfNeeded();
        const processedText = convertInlineBold(trimmedLine);
        html += `<p class="chakra-text css-1p50l3a">${processedText}</p>\n`;
      }
    }
    
    // Close any remaining tags
    if (inCodeBlock) {
      html += `<pre style="border: 1px solid #d1d5db; border-radius: 4px; padding: 12px; background-color: #f9fafb; font-family: monospace; overflow-x: auto;"><code>${codeBlockContent}</code></pre>\n`;
    }
    if (inList) {
      html += '</ul>\n';
    }
    
    return html.trim();
  };

  const parseConversations = (input) => {
    const parts = input.split(/---[QR]/);
    const conversations = [];
    
    for (let i = 1; i < parts.length; i += 2) {
      if (i + 1 < parts.length) {
        const response = parts[i + 1].trim();
        
        // Parse multiple agent blocks within the same response
        const agentBlocks = [];
        const lines = response.split('\n');
        let currentBlock = null;
        let contentLines = [];
        
        for (let j = 0; j < lines.length; j++) {
          const line = lines[j].trim();
          
          if (line.startsWith('AGENT:')) {
            // Save previous block if exists
            if (currentBlock) {
              currentBlock.content = contentLines.join('\n').trim();
              agentBlocks.push(currentBlock);
              contentLines = [];
            }
            
            // Start new block
            currentBlock = {
              agent: line.replace('AGENT:', '').trim(),
              tools: [],
              thinking: null,
              content: ''
            };
          } else if (line.startsWith('TOOLS:') && currentBlock) {
            currentBlock.tools = line.replace('TOOLS:', '').split(',').map(t => t.trim());
          } else if (line.startsWith('THINKING:') && currentBlock) {
            currentBlock.thinking = line.replace('THINKING:', '').trim();
          } else if (!line.startsWith('AGENT:') && !line.startsWith('TOOLS:') && !line.startsWith('THINKING:')) {
            contentLines.push(lines[j]); // Keep original line with spacing
          }
        }
        
        // Save the last block
        if (currentBlock) {
          currentBlock.content = contentLines.join('\n').trim();
          agentBlocks.push(currentBlock);
        }
        
        // If no agent blocks found, create a default one
        if (agentBlocks.length === 0) {
          agentBlocks.push({
            agent: null,
            tools: [],
            thinking: null,
            content: response
          });
        }
        
        conversations.push({
          question: parts[i].trim(),
          agentBlocks: agentBlocks
        });
      }
    }
    
    return conversations;
  };

  const generateScript = async () => {
    try {
      setError('');

      const resolvedAgentImageHref = await getResolvedAgentImageHref();
      
      const conversations = parseConversations(markdownInput);
      
      if (conversations.length === 0) {
        throw new Error('No conversations found. Use ---Q and ---R separators.');
      }

      const conversationData = conversations.map(conv => {
        // Combine all content from agent blocks and convert to HTML
        const combinedContent = conv.agentBlocks.map(block => block.content).join('\n\n');
        const htmlResponse = convertMarkdownToHtml(combinedContent);
        
        return {
          question: conv.question,
          response: htmlResponse,
          agentBlocks: conv.agentBlocks
        };
      });

      // Generate the simplified script
      const script = `// Auto-generated Marvis Multi-Chat Simulation - Simplified Single Box
(function() {
    const CONFIG = {
        inputTypingSpeed: ${inputSpeed},
        responseTypingSpeed: ${responseSpeed},
        pauseBetweenResponses: ${pauseTime},
      stageTime: ${stageTime},
      customInputSelector: ${JSON.stringify(inputSelector)},
      agentImageHref: ${JSON.stringify(resolvedAgentImageHref)}
    };
    
    const conversations = ${JSON.stringify(conversationData, null, 4)};
    
    let chatContainer = null;
    let inputField = null;
    let currentConversationIndex = 0;
    
    function convertMarkdownToHtml(markdown) {
        let html = '';
        const lines = markdown.split('\\n');
        let inList = false;
        let inOrderedSection = false;
        let inCodeBlock = false;
        let codeBlockContent = '';
        
        const autoLinkUrls = (text) => {
          return text.replace(new RegExp('https?:\\/\\/[^ <]+', 'g'), (url) => {
            const match = url.match(new RegExp('^(.*?)([.,!?;:)]*)$'));
            const href = match ? match[1] : url;
            const trailing = match ? match[2] : '';
            return '<a href="' + href + '" target="_blank" rel="noopener noreferrer">' + href + '</a>' + trailing;
          });
        };

        const convertInlineBold = (text) => {
          return autoLinkUrls(text.replace(/\\*\\*(.*?)\\*\\*/g, '<strong>$1</strong>'));
        };
        const isRawHtmlLine = (text) => /^<\\/?[a-zA-Z][^>]*>/.test(text);

        const isTableRow = (text) => /^\\|(.+\\|)+$/.test(text);
        const isTableSeparator = (text) => /^\\|?\\s*:?-{3,}:?\\s*(\\|\\s*:?-{3,}:?\\s*)+\\|?$/.test(text);
        const parseTableCells = (text) => text.replace(/^\\||\\|$/g, '').split('|').map(cell => cell.trim());

        const closeListIfNeeded = () => {
          if (inList) {
            html += '</ul>\\n';
            inList = false;
          }
        };
        
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const trimmedLine = line.trim();
            
            // Handle code blocks
            if (trimmedLine.startsWith('\`\`\`')) {
                if (!inCodeBlock) {
                    // Start of code block
                    inCodeBlock = true;
                    codeBlockContent = '';
                    closeListIfNeeded();
                } else {
                    // End of code block
                    html += \`<pre style="border: 1px solid #d1d5db; border-radius: 4px; padding: 12px; background-color: #f9fafb; font-family: monospace; overflow-x: auto;"><code>\${codeBlockContent}</code></pre>\\n\`;
                    inCodeBlock = false;
                    codeBlockContent = '';
                }
                continue;
            }
            
            if (inCodeBlock) {
                codeBlockContent += line + '\\n';
                continue;
            }

            if (isRawHtmlLine(trimmedLine)) {
              closeListIfNeeded();
              html += line + '\\n';
              continue;
            }

            // Handle markdown tables
            if (isTableRow(trimmedLine) && i + 1 < lines.length && isTableSeparator(lines[i + 1].trim())) {
              closeListIfNeeded();

              const headers = parseTableCells(trimmedLine);
              const rows = [];
              const computedColumnWidth = Math.max(100 / Math.max(headers.length, 1), 33.333);
              i += 2;

              while (i < lines.length) {
                const rowLine = lines[i].trim();
                if (!isTableRow(rowLine) || isTableSeparator(rowLine)) {
                  i--;
                  break;
                }
                rows.push(parseTableCells(rowLine));
                i++;
              }

              html += '<table style="width: 100%; table-layout: fixed; border-collapse: collapse;">\\n<thead>\\n<tr>';
              headers.forEach((header) => {
                html += '<th style="width: ' + computedColumnWidth + '%; min-width: 33.333%;">' + convertInlineBold(header) + '</th>';
              });
              html += '</tr>\\n</thead>\\n<tbody>\\n';

              rows.forEach((row) => {
                html += '<tr>';
                row.forEach((cell) => {
                  html += '<td style="width: ' + computedColumnWidth + '%; min-width: 33.333%;">' + convertInlineBold(cell) + '</td>';
                });
                html += '</tr>\\n';
              });

              html += '</tbody>\\n</table>\\n';
              continue;
            }
            
            // Skip empty lines
            if (!trimmedLine) {
              closeListIfNeeded();
                continue;
            }
            
            // Handle H2 headings as bold title lines
            if (trimmedLine.startsWith('## ')) {
              closeListIfNeeded();
                const title = trimmedLine.substring(3);
              html += \`<p><strong>\${title}</strong></p>\\n\`;
            }
            // Handle bullet points with bold labels
            else if (trimmedLine.startsWith('- **') && trimmedLine.includes(':**')) {
              if (!inList) {
                html += '<ul>\\n';
                inList = true;
              }
                const colonPos = trimmedLine.indexOf(':**');
                if (colonPos > 4) {
                    const label = trimmedLine.substring(4, colonPos);
                    const text = convertInlineBold(trimmedLine.substring(colonPos + 4));
                    html += \`<li style="margin-left: 20px;"><strong>\${label}:</strong> \${text}</li>\\n\`;
                } else {
                    const text = convertInlineBold(trimmedLine.substring(2));
                    html += \`<li style="margin-left: 20px;">\${text}</li>\\n\`;
                }
            }
            // Handle regular bullet points
            else if (trimmedLine.startsWith('- ')) {
              if (!inList) {
                html += '<ul>\\n';
                inList = true;
              }
                const text = convertInlineBold(trimmedLine.substring(2));
                html += \`<li style="margin-left: 20px;">\${text}</li>\\n\`;
            }
            // Handle regular paragraphs
            else {
              closeListIfNeeded();
                const processedText = convertInlineBold(trimmedLine);
              html += '<p class="chakra-text css-1p50l3a">' + processedText + '</p>\\n';
            }
        }
        
        // Close any remaining tags
        if (inCodeBlock) {
            html += \`<pre style="border: 1px solid #d1d5db; border-radius: 4px; padding: 12px; background-color: #f9fafb; font-family: monospace; overflow-x: auto;"><code>\${codeBlockContent}</code></pre>\\n\`;
        }
        if (inList) {
          html += '</ul>\\n';
        }
        
        return html.trim();
    }
    
    function findElements() {
        chatContainer = document.querySelector('.mc-Chatlog') || 
                       document.querySelector('[class*="mc-Chatlog"]') ||
                       document.querySelector('.chatbot-Log') ||
                       document.querySelector('[role="log"]');

        const preferredSelector = (CONFIG.customInputSelector && CONFIG.customInputSelector.trim())
          ? CONFIG.customInputSelector.trim()
          : '';

        const active = document.activeElement;
        const activeIsEditable = !!(active && (
          active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.isContentEditable ||
          active.getAttribute('role') === 'textbox'
        ));

        inputField = (preferredSelector ? document.querySelector(preferredSelector) : null) ||
                    (activeIsEditable ? active : null) ||
                    document.querySelector('textarea:not([disabled])') ||
                    document.querySelector('input:not([disabled]):not([type="hidden"])') ||
                    document.querySelector('[contenteditable="true"]') ||
                    document.querySelector('[role="textbox"]');
        
        if (!chatContainer) {
            console.error('Could not find chat container');
            return false;
        }
        
        if (!inputField) {
          console.warn('Input field not found at launch. Will allow manual selection during typing.');
        }
        
        console.log('Found elements:', {
          chatContainer: chatContainer.className,
          inputField: inputField ? inputField.className : null
        });
        return true;
    }
    
    function simulateInputTyping(text, callback) {
        const targetSelector = (CONFIG.customInputSelector && CONFIG.customInputSelector.trim())
          ? CONFIG.customInputSelector.trim()
          : '';

        const getHumanizedInputDelay = (() => {
          let rhythmDrift = 0;

          return () => {
            const base = Math.max(1, Number(CONFIG.inputTypingSpeed) || 1);

            // Keep variation centered around the configured speed while adding short bursts/slows.
            const driftStep = (Math.random() - 0.5) * 0.28;
            rhythmDrift = Math.max(-0.18, Math.min(0.18, rhythmDrift * 0.65 + driftStep));

            const jitter = (Math.random() - 0.5) * 0.16;
            const multiplier = 1 + rhythmDrift + jitter;
            return Math.max(5, Math.round(base * multiplier));
          };
        })();

        const syncTypedInputViewport = (el, isTextInput, isContentEditable) => {
          if (isTextInput) {
            const end = el.value.length;

            if (typeof el.setSelectionRange === 'function') {
              try {
                el.setSelectionRange(end, end);
              } catch (e) {
                // Some input types do not allow programmatic selection.
              }
            }

            if (typeof el.scrollLeft === 'number') {
              el.scrollLeft = el.scrollWidth;
            }
            if (typeof el.scrollTop === 'number') {
              el.scrollTop = el.scrollHeight;
            }

            return;
          }

          if (!isContentEditable) {
            return;
          }

          const selection = window.getSelection();
          if (selection) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
          }

          if (typeof el.scrollLeft === 'number') {
            el.scrollLeft = el.scrollWidth;
          }
          if (typeof el.scrollTop === 'number') {
            el.scrollTop = el.scrollHeight;
          }
        };

        const resolveInputElement = () => {
          if (targetSelector) {
            const bySelector = document.querySelector(targetSelector);
            if (bySelector) return bySelector;
          }

          if (inputField && inputField.isConnected) {
            return inputField;
          }

          const active = document.activeElement;
          const activeIsEditable = !!(active && (
            active.tagName === 'INPUT' ||
            active.tagName === 'TEXTAREA' ||
            active.isContentEditable ||
            active.getAttribute('role') === 'textbox'
          ));
          if (activeIsEditable) {
            return active;
          }

          return document.querySelector('textarea:not([disabled])') ||
                 document.querySelector('input:not([disabled]):not([type="hidden"])') ||
                 document.querySelector('[contenteditable="true"]') ||
                 document.querySelector('[role="textbox"]');
        };

        const runSimulation = async (el) => {
          el.focus();

          const isTextInput = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA';
          const isContentEditable = !!el.isContentEditable;

          if (isTextInput) {
            el.value = '';
          } else if (isContentEditable) {
            el.textContent = '';
          }

          for (let i = 0; i < text.length; i++) {
            if (isTextInput) {
              el.value += text[i];
            } else if (isContentEditable) {
              el.textContent += text[i];
            }
            syncTypedInputViewport(el, isTextInput, isContentEditable);
            el.dispatchEvent(new Event('input', { bubbles: true }));
            await new Promise(res => setTimeout(res, getHumanizedInputDelay()));
          }

          setTimeout(() => {
            if (isTextInput) {
              el.value = '';
            } else if (isContentEditable) {
              el.textContent = '';
            }
            if (callback) callback();
          }, 500 + Math.random() * 500);
        };

        const autoElement = resolveInputElement();

        if (autoElement) {
          inputField = autoElement;
          runSimulation(autoElement);
          return;
        }

        console.log('Input field not resolved. Click the desired input element manually.');

        const mouseOverHandler = (e) => {
          const isSelectable = !!(e.target && (
            e.target.tagName === 'INPUT' ||
            e.target.tagName === 'TEXTAREA' ||
            e.target.isContentEditable ||
            e.target.getAttribute('role') === 'textbox'
          ));

          if (isSelectable) {
            e.target.style.outline = '2px solid #007bff';
          }
        };

        const mouseOutHandler = (e) => {
          const isSelectable = !!(e.target && (
            e.target.tagName === 'INPUT' ||
            e.target.tagName === 'TEXTAREA' ||
            e.target.isContentEditable ||
            e.target.getAttribute('role') === 'textbox'
          ));

          if (isSelectable) {
            e.target.style.outline = '';
          }
        };

        const clickHandler = async (e) => {
          const isSelectable = !!(e.target && (
            e.target.tagName === 'INPUT' ||
            e.target.tagName === 'TEXTAREA' ||
            e.target.isContentEditable ||
            e.target.getAttribute('role') === 'textbox'
          ));

          if (!isSelectable) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();

          document.removeEventListener('click', clickHandler, true);
          document.removeEventListener('mouseover', mouseOverHandler, true);
          document.removeEventListener('mouseout', mouseOutHandler, true);
          e.target.style.outline = '';

          inputField = e.target;
          await runSimulation(e.target);
        };

        document.addEventListener('mouseover', mouseOverHandler, true);
        document.addEventListener('mouseout', mouseOutHandler, true);
        document.addEventListener('click', clickHandler, true);
    }
    
    function createUserQuestion(questionText) {
        const responseGroup = document.createElement("div");
        responseGroup.className = "mc-Chatlog-responseGroup mc-Chatlog-responseGroup--animated";
        
        const response = document.createElement("div");
        response.className = "mc-Chatlog-response mc-Chatlog-response--self css-0";
        response.setAttribute("role", "comment");
        
        const bubble = document.createElement("div");
        bubble.className = "mc-ChatBubble mc-ChatBubble--padded";
        
        const content = document.createElement("div");
        content.className = "mc-ChatBubble-content css-8atqhb";
        
        const text = document.createElement("p");
        text.className = "text";
        text.textContent = questionText;
        
        content.appendChild(text);
        bubble.appendChild(content);
        response.appendChild(bubble);
        responseGroup.appendChild(response);
        chatContainer.appendChild(responseGroup);
    }

      function setToolCallText(targetElement, toolName) {
        if (!targetElement) return;

        targetElement.innerHTML = "";
        const hasCustomIcon = typeof CONFIG.agentImageHref === "string" && CONFIG.agentImageHref.trim() !== "";

        if (hasCustomIcon) {
          const icon = document.createElement("img");
          icon.src = CONFIG.agentImageHref;
          icon.alt = "Tool";
          icon.width = 48;
          icon.height = 48;
          icon.style.verticalAlign = "middle";
          icon.style.marginRight = "12px";
          icon.style.borderRadius = "3px";
          targetElement.appendChild(icon);
        }

        const toolCallLabel = document.createElement("strong");
        toolCallLabel.textContent = "Tool Call:";
        targetElement.appendChild(toolCallLabel);
        targetElement.appendChild(document.createTextNode(" " + toolName));
      }
    
    function createAgentToolCall(toolName) {
        const responseGroup = document.createElement("div");
        responseGroup.className = "mc-Chatlog-responseGroup mc-Chatlog-responseGroup--animated";
        
        const response = document.createElement("div");
        response.className = "mc-Chatlog-response css-0";
        response.setAttribute("role", "comment");
        
        const bubble = document.createElement("div");
        bubble.className = "mc-ChatBubble mc-ChatBubble--padded mc-chatbot-agent";
        bubble.style.lineHeight = "3.5";
        bubble.style.maxWidth = "100%";
        
        const bubbleContent = document.createElement("div");
        bubbleContent.className = "mc-ChatBubble-content css-8atqhb";

        let toggleButton = null;
        let toggleLabel = null;
        let toggleIconPath = null;
        
        const agentResponse = document.createElement("div");
        agentResponse.className = "mc-Chatlog-response mc-Chatlog-response--agent css-0";
        agentResponse.setAttribute("role", "comment");
        
        const wrapper1 = document.createElement("div");
        wrapper1.className = "css-0";
        
        const wrapper2 = document.createElement("div");
        wrapper2.className = "css-79elbk";
        
        const stack = document.createElement("div");
        stack.className = "chakra-stack css-1ah53el";
        
        const textDiv = document.createElement("div");
        textDiv.className = "mc-Chatlog-response--agent-text";

        const toolText = document.createElement("p");
        setToolCallText(toolText, toolName);
        textDiv.appendChild(toolText);
        
        stack.appendChild(textDiv);
        wrapper2.appendChild(stack);
        wrapper1.appendChild(wrapper2);

        const allToolsContainer = document.createElement("div");
        allToolsContainer.className = "css-0";
        allToolsContainer.style.display = "none";

        agentResponse.appendChild(wrapper1);
        agentResponse.appendChild(allToolsContainer);
        bubbleContent.appendChild(agentResponse);
        bubble.appendChild(bubbleContent);
        response.appendChild(bubble);
        responseGroup.appendChild(response);

        const toolCallUi = {
          responseGroup,
          toolTextElement: toolText,
          toggleButton,
          toggleLabel,
          toggleIconPath,
          allToolsContainer,
          toolHistory: [toolName],
          expanded: false,
          totalTools: 1,
          bubbleContent
        };

        refreshToolCallUi(toolCallUi);
        return toolCallUi;
    }

        function ensureToolToggle(toolCallUi) {
        if (!toolCallUi || toolCallUi.toggleButton || toolCallUi.toolHistory.length <= 1) return;

        const toggleButton = document.createElement("button");
        toggleButton.setAttribute("type", "button");
        toggleButton.className = "chakra-button css-1ywb400";

        const toggleLabel = document.createTextNode("");
        toggleButton.appendChild(toggleLabel);

        const buttonIcon = document.createElement("span");
        buttonIcon.className = "chakra-button__icon css-1hzyiq5";

        const buttonSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        buttonSvg.setAttribute("fill", "currentcolor");
        buttonSvg.setAttribute("viewBox", "0 0 32 32");
        buttonSvg.setAttribute("role", "img");
        buttonSvg.setAttribute("class", "uikit-icon-chevronDown");
        buttonSvg.setAttribute("aria-hidden", "true");
        buttonSvg.setAttribute("focusable", "false");
        buttonSvg.style.width = "12px";
        buttonSvg.style.height = "12px";

        const toggleIconPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
        buttonSvg.appendChild(toggleIconPath);
        buttonIcon.appendChild(buttonSvg);
        toggleButton.appendChild(buttonIcon);

        toggleButton.addEventListener("click", function() {
          toolCallUi.expanded = !toolCallUi.expanded;
          refreshToolCallUi(toolCallUi);
        });

        toolCallUi.toggleButton = toggleButton;
        toolCallUi.toggleLabel = toggleLabel;
        toolCallUi.toggleIconPath = toggleIconPath;
        toolCallUi.bubbleContent.insertBefore(toggleButton, toolCallUi.bubbleContent.firstChild);
        }

        function refreshToolCallUi(toolCallUi) {
        if (!toolCallUi || !toolCallUi.toolTextElement) return;

        const latestTool = toolCallUi.toolHistory[toolCallUi.toolHistory.length - 1] || "";
        setToolCallText(toolCallUi.toolTextElement, latestTool);
        toolCallUi.totalTools = toolCallUi.toolHistory.length;

        ensureToolToggle(toolCallUi);

        if (toolCallUi.toggleButton && toolCallUi.toggleLabel && toolCallUi.toggleIconPath) {
          if (toolCallUi.expanded) {
            toolCallUi.toggleButton.setAttribute("aria-label", "Show latest response");
            toolCallUi.toggleLabel.nodeValue = "Show latest response";
            toolCallUi.toggleIconPath.setAttribute("d", "M1 20 L16 6 L31 20 L27 24 L16 14 L5 24 z");
          } else {
            toolCallUi.toggleButton.setAttribute("aria-label", "Show all responses (" + toolCallUi.totalTools + ")");
            toolCallUi.toggleLabel.nodeValue = "Show all responses (" + toolCallUi.totalTools + ")";
            toolCallUi.toggleIconPath.setAttribute("d", "M1 12 L16 26 L31 12 L27 8 L16 18 L5 8 z");
          }
        }

        if (toolCallUi.allToolsContainer) {
          if (!toolCallUi.expanded || toolCallUi.toolHistory.length <= 1) {
            toolCallUi.allToolsContainer.style.display = "none";
            toolCallUi.allToolsContainer.innerHTML = "";
          } else {
            toolCallUi.allToolsContainer.style.display = "block";
            toolCallUi.allToolsContainer.innerHTML = "";

            for (let i = 0; i < toolCallUi.toolHistory.length; i++) {
              const itemWrap = document.createElement("div");
              itemWrap.className = "css-79elbk";
              itemWrap.style.marginTop = "8px";

              const divider = document.createElement("div");
              divider.className = "css-c2imvx";

              const itemStack = document.createElement("div");
              itemStack.className = "chakra-stack css-1ah53el";

              const itemTextWrap = document.createElement("div");
              itemTextWrap.className = "mc-Chatlog-response--agent-text";

              const itemText = document.createElement("p");
              setToolCallText(itemText, toolCallUi.toolHistory[i]);
              itemTextWrap.appendChild(itemText);

              itemStack.appendChild(itemTextWrap);
              itemWrap.appendChild(divider);
              itemWrap.appendChild(itemStack);
              toolCallUi.allToolsContainer.appendChild(itemWrap);
            }
          }
        }
        }

        function updateAgentToolCall(toolCallUi, toolName) {
        if (!toolCallUi) return;
        toolCallUi.toolHistory.push(toolName);
        refreshToolCallUi(toolCallUi);
        }
    
    function createFinalResponse(htmlContent) {
        const responseGroup = document.createElement("div");
        responseGroup.className = "mc-Chatlog-responseGroup mc-Chatlog-responseGroup--animated";
        
        const response = document.createElement("div");
        response.className = "mc-Chatlog-response css-0";
        response.setAttribute("role", "comment");
        
        const bubble = document.createElement("div");
        bubble.className = "mc-ChatBubble mc-ChatBubble--padded";
        
        const content = document.createElement("div");
        content.className = "mc-ChatBubble-content css-8atqhb";
        
        const textContainer = document.createElement("div");
        textContainer.className = "text";
        
        content.appendChild(textContainer);
        bubble.appendChild(content);
        response.appendChild(bubble);
        responseGroup.appendChild(response);
        
        return { responseGroup, textContainer };
    }
    
    function scrollChatToBottom() {
        const chatLog = document.querySelector(".mc-Chatlog") || document.querySelector(".chatbot-Log");
        if (chatLog) {
            chatLog.scrollTop = chatLog.scrollHeight;
        }
    }
    
    function simulateTypingInElement(element, htmlContent, callback) {
      const parserContainer = document.createElement('div');
      parserContainer.innerHTML = htmlContent;
      element.innerHTML = '';

      function getCharDelay(char) {
        let delay = CONFIG.responseTypingSpeed;
        if (char.match(/[.!?]/)) delay *= 2.5;
        else if (char === ' ') delay *= 0.4;
        else if (char.match(/[,;:]/)) delay *= 1.5;
        delay += (Math.random() - 0.5) * delay * 0.3;
        return delay;
      }

      function typeTextIntoNode(targetNode, text, done) {
        if (!text || !text.length) {
          done();
          return;
        }

        let index = 0;
        function typeChar() {
          if (index >= text.length) {
            done();
            return;
          }

          const char = text[index];
          targetNode.textContent += char;
          index++;

          if (index % 30 === 0) {
            scrollChatToBottom();
          }

          setTimeout(typeChar, getCharDelay(char));
        }

        typeChar();
      }

      function processNode(sourceNode, targetParent, done) {
        if (sourceNode.nodeType === Node.TEXT_NODE) {
          const textNode = document.createTextNode('');
          targetParent.appendChild(textNode);
          typeTextIntoNode(textNode, sourceNode.textContent || '', done);
          return;
        }

        if (sourceNode.nodeType !== Node.ELEMENT_NODE) {
          done();
          return;
        }

        const tagName = sourceNode.tagName ? sourceNode.tagName.toLowerCase() : '';

        if (tagName === 'table') {
          targetParent.appendChild(sourceNode.cloneNode(true));
          scrollChatToBottom();
          const tablePause = Math.max(300, Math.floor(CONFIG.stageTime / 2));
          setTimeout(done, tablePause);
          return;
        }

        const clone = sourceNode.cloneNode(false);
        targetParent.appendChild(clone);

        const children = Array.from(sourceNode.childNodes);
        let childIndex = 0;

        function nextChild() {
          if (childIndex >= children.length) {
            done();
            return;
          }

          processNode(children[childIndex], clone, function() {
            childIndex++;
            nextChild();
          });
        }

        nextChild();
      }

      const rootNodes = Array.from(parserContainer.childNodes);
      let rootIndex = 0;

      function processNextRootNode() {
        if (rootIndex >= rootNodes.length) {
          scrollChatToBottom();
          setTimeout(() => { if (callback) callback(); }, CONFIG.pauseBetweenResponses);
          return;
        }

        processNode(rootNodes[rootIndex], element, function() {
          rootIndex++;
          processNextRootNode();
        });
      }

      processNextRootNode();
    }
    
    async function runConversation(index, callback) {
        const conv = conversations[index];
        console.log(\`Starting conversation \${index + 1}: "\${conv.question}"\`);
        
        if (inputField) {
            inputField.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        
        simulateInputTyping(conv.question, function() {
            console.log("Input typing complete - creating simulated user message");
            
            createUserQuestion(conv.question);
            scrollChatToBottom();
            
            setTimeout(function() {
                console.log("Starting agent response simulation...");
                
                // Process each agent block sequentially with tool calls
                let blockIndex = 0;
                
                function processNextBlock() {
                    if (blockIndex < conv.agentBlocks.length) {
                        const block = conv.agentBlocks[blockIndex];
                        
                        // Determine tool calls to display
                        const toolCalls = [];
                        if (block.tools && block.tools.length > 0) {
                            block.tools.forEach(function(tool) { toolCalls.push(tool); });
                        }

                        const totalToolStageTime = Math.max(0, CONFIG.stageTime);
                        const perToolDelay = toolCalls.length > 0
                          ? Math.max(50, Math.floor(totalToolStageTime / toolCalls.length))
                          : 0;
                        
                        let toolIndex = 0;
                        let toolCallUi = null;
                        
                        function showNextToolCall() {
                            if (toolIndex < toolCalls.length) {
                            if (toolIndex === 0) {
                              toolCallUi = createAgentToolCall(toolCalls[toolIndex]);
                              chatContainer.appendChild(toolCallUi.responseGroup);
                            } else {
                              updateAgentToolCall(toolCallUi, toolCalls[toolIndex]);
                            }
                                scrollChatToBottom();
                                toolIndex++;
                                setTimeout(showNextToolCall, perToolDelay);
                            } else {
                                showBlockContent();
                            }
                        }
                        
                        function showBlockContent() {
                            if (block.content.trim()) {
                                const blockHtml = convertMarkdownToHtml(block.content);
                                const { responseGroup: contentGroup, textContainer } = createFinalResponse(blockHtml);
                                chatContainer.appendChild(contentGroup);
                                scrollChatToBottom();
                                
                                simulateTypingInElement(textContainer, blockHtml, function() {
                                    blockIndex++;
                                    setTimeout(processNextBlock, 1000);
                                });
                            } else {
                                blockIndex++;
                                setTimeout(processNextBlock, 500);
                            }
                        }
                        
                        if (toolCalls.length > 0) {
                            showNextToolCall();
                        } else {
                            showBlockContent();
                        }
                    } else {
                        if (callback) callback();
                    }
                }
                
                processNextBlock();
            }, 300);
        });
    }
    
    function runAllConversations() {
        if (!findElements()) {
            console.error("Could not find required elements");
            return;
        }
        
        function nextConversation() {
            if (currentConversationIndex < conversations.length) {
                runConversation(currentConversationIndex, () => {
                    currentConversationIndex++;
                    if (currentConversationIndex < conversations.length) {
                        setTimeout(nextConversation, 1000);
                    } else {
                        console.log("All conversations completed!");
                    }
                });
            }
        }
        
        nextConversation();
    }
    
    console.log(\`Starting \${conversations.length} conversation simulation with agent tool calls!\`);
    runAllConversations();
})();`;

      setJsOutput(script);
      await copyToClipboard(script);
      
    } catch (err) {
      setError(err.message);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      setError(err.message || 'Failed to copy to clipboard');
    }
  };

  const testMarkdown = () => {
    const testText = `The problem stems from **BGP route instability** on spine switch DC1-SPINE-02.

## Optical degradation analysis

- **Receive optical power:** Has degraded by 4.2 dBm over the last week and is now at -12.8 dBm, approaching the **receiver sensitivity threshold** of -14 dBm
- **Laser bias current:** Has increased by 18% during the same period

AGENT: Network Diagnostics Specialist
TOOLS: interface_analysis, optical_diagnostics
THINKING: Analyzing interface statistics and optical parameters...

Regular paragraph with **inline bold text** should work now.`;
    
    const conversations = parseConversations(`---Q\nTest question?\n\n---R\n${testText}`);
    console.log('Test conversations:', conversations);
    alert('Check console for parsed conversation data with agent, tools, and thinking metadata!');
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div style={{ backgroundColor: '#dbeafe', padding: '16px', borderRadius: '8px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e3a8a', marginBottom: '8px' }}>
            Marvis Chat Configurator - Agent Tool Calls
          </h1>
          <p style={{ color: '#1e40af', margin: 0 }}>
            Create conversations with agent tool call indicators matching the chatbot UI
          </p>
        </div>
        <a
          href={`${process.env.PUBLIC_URL || ''}/marvis-chat-tool-source.zip`}
          download
          title="Download the complete source code as a .zip"
          style={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: '#1e3a8a',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '6px',
            fontWeight: 600,
            fontSize: '14px',
            whiteSpace: 'nowrap'
          }}
        >
          <span aria-hidden="true">⬇</span> Download Code
        </a>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Input Section */}
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Configuration</h2>
          
          {/* Speed Controls */}
          <div style={{ backgroundColor: '#f9fafb', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
            <h3 style={{ fontWeight: '500', marginBottom: '12px' }}>Timing Settings</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                  Input Speed (ms):
                </label>
                <input
                  type="number"
                  value={inputSpeed}
                  onChange={(e) => setInputSpeed(parseInt(e.target.value))}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                  Response Speed (ms):
                </label>
                <input
                  type="number"
                  value={responseSpeed}
                  onChange={(e) => setResponseSpeed(parseInt(e.target.value))}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                  Pause Time (ms):
                </label>
                <input
                  type="number"
                  value={pauseTime}
                  onChange={(e) => setPauseTime(parseInt(e.target.value))}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                  Tool Call Time (ms):
                </label>
                <input
                  type="number"
                  value={stageTime}
                  onChange={(e) => setStageTime(parseInt(e.target.value))}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                Input CSS Selector (optional):
              </label>
              <input
                type="text"
                value={inputSelector}
                onChange={(e) => setInputSelector(e.target.value)}
                placeholder=".chakra-input__group input.chakra-input"
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px' }}
              />
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                Agent Image Href (optional):
              </label>
              <textarea
                value={agentImageHref}
                onChange={(e) => setAgentImageHref(e.target.value)}
                placeholder="data:image/png;base64,..."
                style={{ width: '100%', minHeight: '88px', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px' }}
              />
            </div>
          </div>

          {/* Markdown Input */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
              Conversations (Markdown + Metadata):
            </label>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
              <strong>Format:</strong> Agent tool calls display as SVG avatar + tool name, matching the live chatbot UI
            </div>
            <textarea
              value={markdownInput}
              onChange={(e) => setMarkdownInput(e.target.value)}
              style={{ 
                width: '100%', 
                height: '400px', 
                padding: '12px', 
                border: '1px solid #d1d5db', 
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px'
              }}
              placeholder="---Q
Your question here?

---R
Your response here...

AGENT: Network Troubleshooting Specialist
TOOLS: network_diagnostics, interface_analysis
THINKING: Analyzing network topology and recent alerts..."
            />
          </div>

          {error && (
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#fef2f2', 
              border: '1px solid #fecaca', 
              borderRadius: '4px',
              color: '#dc2626',
              marginTop: '12px'
            }}>
              {error}
            </div>
          )}

          <button
            onClick={generateScript}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontWeight: '500',
              marginTop: '16px',
              cursor: 'pointer'
            }}
          >
            Generate Script
          </button>
          
          <button
            onClick={testMarkdown}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              marginTop: '8px',
              cursor: 'pointer'
            }}
          >
            Test Conversation Parsing
          </button>
        </div>

        {/* Output Section */}
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Generated Script</h2>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h3 style={{ fontWeight: '500' }}>Simplified JavaScript</h3>
            <button
              onClick={() => copyToClipboard(jsOutput)}
              disabled={!jsOutput}
              style={{
                padding: '6px 12px',
                backgroundColor: jsOutput ? '#059669' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: jsOutput ? 'pointer' : 'not-allowed'
              }}
            >
              Copy Script
            </button>
          </div>
          
          <textarea
            value={jsOutput}
            readOnly
            style={{
              width: '100%',
              height: '650px',
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: '#f9fafb',
              fontFamily: 'monospace',
              fontSize: '12px'
            }}
            placeholder="Generated JavaScript will appear here..."
          />

          {/* Reusable pre-prompt for generating new conversations with another LLM */}
          <div style={{ marginTop: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h3 style={{ fontWeight: '500' }}>Reusable LLM Pre-Prompt</h3>
              <button
                onClick={() => copyToClipboard(LLM_PRE_PROMPT)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Copy Pre-Prompt
              </button>
            </div>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: 0, marginBottom: '8px' }}>
              Paste this into any other LLM (ChatGPT, Claude, Gemini…) to have it generate a brand-new
              conversation in the exact <strong>---Q / ---R</strong> + <strong>AGENT / TOOLS / THINKING</strong>{' '}
              format this tool expects. Add your scenario at the end, then paste the LLM's output into the editor on the left.
            </p>
            <textarea
              value={LLM_PRE_PROMPT}
              readOnly
              style={{
                width: '100%',
                height: '280px',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: '#f9fafb',
                fontFamily: 'monospace',
                fontSize: '12px'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarvisChatConfigurator;